import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X, Sparkles, Star, Loader2, BookImage } from 'lucide-react'
import { getAutoAlbums, applyAlbumPicks, type AutoAlbum } from '../../shared/api/albums'
import { mediaKeys } from '../live/useToday'
import { Button } from '../../shared/components/Button'

interface Props {
  tripId: string
  onClose: () => void
  onApplied?: (count: number) => void
}

export function AutoAlbumsPanel({ tripId, onClose, onApplied }: Props) {
  const qc = useQueryClient()

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['auto-albums', tripId],
    queryFn: () => getAutoAlbums(tripId),
  })

  const applyMut = useMutation({
    mutationFn: (ids: string[]) => applyAlbumPicks(tripId, ids),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: mediaKeys.byTrip(tripId) })
      onApplied?.(result.updated)
    },
  })

  const allBestPicks = data?.albums.flatMap((a) => a.best_pick_ids) ?? []

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-2xl bg-st-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-st-line px-5 py-4">
          <div className="flex items-center gap-2 font-semibold text-st-text">
            <Sparkles className="h-5 w-5 text-st-primary" />
            AI Auto Albums
          </div>
          <button type="button" onClick={onClose} className="text-st-muted hover:text-st-text" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {isLoading || isFetching ? (
            <div className="flex items-center justify-center gap-2 py-16 text-st-muted">
              <Loader2 className="h-5 w-5 animate-spin" />
              Grouping photos by day and place…
            </div>
          ) : isError ? (
            <div className="py-12 text-center">
              <p className="text-st-danger">Could not generate albums.</p>
              <Button variant="ghost" className="mt-3" onClick={() => void refetch()}>
                Try again
              </Button>
            </div>
          ) : !data || data.album_count === 0 ? (
            <div className="py-12 text-center text-st-muted">
              <BookImage className="mx-auto mb-3 h-10 w-10 opacity-40" />
              <p className="font-medium text-st-text">No photos to curate yet</p>
              <p className="mt-1 text-sm">Upload some trip photos first, then come back.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-st-muted">
                {data.total_photos} photos grouped into {data.album_count} album
                {data.album_count === 1 ? '' : 's'}. Stars mark AI best picks for your story.
              </p>
              {data.albums.map((album) => (
                <AlbumCard key={album.id} album={album} />
              ))}
            </div>
          )}
        </div>

        {data && data.album_count > 0 && (
          <div className="flex items-center justify-between gap-3 border-t border-st-line px-5 py-4">
            <p className="text-xs text-st-muted">
              Adds {allBestPicks.length} best pick{allBestPicks.length === 1 ? '' : 's'} to your web story
            </p>
            <Button
              variant="accent"
              disabled={applyMut.isPending || allBestPicks.length === 0}
              onClick={() => applyMut.mutate(allBestPicks)}
            >
              {applyMut.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Applying…
                </>
              ) : (
                <>Add best picks to story</>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

function AlbumCard({ album }: { album: AutoAlbum }) {
  const bestSet = new Set(album.best_pick_ids)

  return (
    <div className="rounded-st-lg border border-st-line bg-st-surface-2 p-4">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <h3 className="font-display font-semibold text-st-text">{album.title}</h3>
          <p className="text-xs text-st-muted">{album.subtitle}</p>
        </div>
        {album.day_index !== null && (
          <span className="shrink-0 rounded-st-full bg-st-primary/10 px-2 py-0.5 text-xs font-medium text-st-primary">
            Day {album.day_index + 1}
          </span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
        {album.media.map((m) => (
          <div key={m.id} className="relative aspect-square overflow-hidden rounded-st-sm">
            <img src={m.url} alt="" className="h-full w-full object-cover" />
            {bestSet.has(m.id) && (
              <span
                className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-amber-400 text-white shadow"
                title="Best pick"
              >
                <Star className="h-3 w-3 fill-current" />
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export default AutoAlbumsPanel
